import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Sample blog data - replace with your actual data source
const blogPosts = [
  {
    id: 1,
    title: "How AI Receptionists Are Transforming Small Businesses in South Africa",
    excerpt: "Discover how AI-powered phone answering is helping SA businesses capture more leads and improve customer service without breaking the bank.",
    category: "Business Growth",
    author: "Sarah Mitchell",
    date: "Feb 10, 2026",
    readTime: "5 min read",
    image: "/images/blog-1.jpg",
    featured: true
  },
  {
    id: 2,
    title: "5 Signs Your Business Is Losing Money from Missed Calls",
    excerpt: "Learn the warning signs that indicate your business is bleeding revenue through unanswered calls and how to fix it.",
    category: "Tips & Insights",
    author: "James Thompson",
    date: "Feb 8, 2026",
    readTime: "4 min read",
    image: "/images/blog-2.jpg",
    featured: true
  },
  {
    id: 3,
    title: "Case Study: How a Cape Town Estate Agency Increased Leads by 40%",
    excerpt: "Real results from a real business. See how implementing Gerbo helped this agency capture more property inquiries.",
    category: "Case Studies",
    author: "Emma Davis",
    date: "Feb 5, 2026",
    readTime: "7 min read",
    image: "/images/blog-3.jpg",
    featured: false
  },
  {
    id: 4,
    title: "The Complete Guide to 24/7 Customer Service for Small Businesses",
    excerpt: "Everything you need to know about providing round-the-clock support without hiring night shift staff.",
    category: "Guides",
    author: "Michael Chen",
    date: "Feb 3, 2026",
    readTime: "10 min read",
    image: "/images/blog-4.jpg",
    featured: false
  },
  {
    id: 5,
    title: "GDPR Compliance for AI Phone Systems: What SA Businesses Need to Know",
    excerpt: "Navigate the legal landscape of using AI for customer interactions while staying compliant with privacy regulations.",
    category: "Legal & Compliance",
    author: "Lisa Ndlovu",
    date: "Jan 30, 2026",
    readTime: "6 min read",
    image: "/images/blog-5.jpg",
    featured: false
  },
  {
    id: 6,
    title: "Why Restaurants Are Switching to AI Phone Answering",
    excerpt: "The hospitality industry is embracing AI to handle reservations and inquiries. Here's why it works so well.",
    category: "Industry Insights",
    author: "David Palmer",
    date: "Jan 28, 2026",
    readTime: "5 min read",
    image: "/images/blog-6.jpg",
    featured: false
  }
];

const categories = ["All Posts", "Business Growth", "Tips & Insights", "Case Studies", "Guides", "Legal & Compliance", "Industry Insights"];

export const Blog: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("All Posts");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredPosts, setFilteredPosts] = useState(blogPosts);

  useEffect(() => {
    // Header scroll effect
    const handleScroll = () => {
      const header = document.querySelector('.header');
      if (window.scrollY > 50) {
        header?.classList.add('scrolled');
      } else {
        header?.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Filter posts based on category and search
    let filtered = blogPosts;

    if (selectedCategory !== "All Posts") {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredPosts(filtered);
  }, [selectedCategory, searchQuery]);

  const toggleMobileMenu = () => {
    const navMenu = document.querySelector('.nav-menu') as HTMLElement;
    if (navMenu.style.display === 'flex') {
      navMenu.style.display = 'none';
    } else {
      navMenu.style.display = 'flex';
      navMenu.style.flexDirection = 'column';
      navMenu.style.position = 'absolute';
      navMenu.style.top = '100%';
      navMenu.style.left = '0';
      navMenu.style.right = '0';
      navMenu.style.background = 'white';
      navMenu.style.padding = '2rem';
      navMenu.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.1)';
      navMenu.style.zIndex = '1000';
    }
  };

  const featuredPosts = filteredPosts.filter(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700;800&family=Poppins:wght@300;400;500;600;700&display=swap');

        :root {
          --font-heading: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          --font-body: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          --primary: #0A0E27;
          --accent: #00E5A0;
          --accent-secondary: #00B8D4;
          --text-dark: #0A0E27;
          --text-light: #5A607F;
          --text-muted: #9EA4B8;
          --bg-light: #F8FAFB;
          --bg-white: #fcfadd;
          --border: #E5E9F2;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        body {
          font-family: var(--font-body);
          line-height: 1.6;
          color: var(--text-dark);
          background: var(--bg-white);
        }

        /* Blog Hero */
        .blog-hero {
          padding: 160px 2rem 80px;
          background: linear-gradient(135deg, 
            rgba(0, 229, 160, 0.05) 0%, 
            rgba(0, 184, 212, 0.05) 100%),
            var(--bg-white);
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .blog-hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: 
            linear-gradient(var(--border) 1px, transparent 1px),
            linear-gradient(90deg, var(--border) 1px, transparent 1px);
          background-size: 50px 50px;
          opacity: 0.3;
          pointer-events: none;
        }

        .blog-hero-content {
          max-width: 900px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .blog-hero h1 {
          font-family: var(--font-heading);
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 800;
          margin-bottom: 1.5rem;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        .blog-hero .highlight {
          background: linear-gradient(135deg, var(--accent) 0%, var(--accent-secondary) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .blog-hero p {
          font-size: clamp(1.1rem, 2vw, 1.3rem);
          color: var(--text-light);
          margin-bottom: 3rem;
          line-height: 1.7;
        }

        /* Search Bar */
        .blog-search {
          max-width: 600px;
          margin: 0 auto;
          position: relative;
        }

        .blog-search input {
          width: 100%;
          padding: 1.25rem 3.5rem 1.25rem 1.5rem;
          border: 2px solid var(--border);
          border-radius: 16px;
          font-size: 1rem;
          font-family: var(--font-body);
          background: var(--bg-white);
          transition: all 0.3s ease;
        }

        .blog-search input:focus {
          outline: none;
          border-color: var(--accent);
          box-shadow: 0 0 0 4px rgba(0, 229, 160, 0.1);
        }

        .blog-search svg {
          position: absolute;
          right: 1.5rem;
          top: 50%;
          transform: translateY(-50%);
          width: 24px;
          height: 24px;
          color: var(--text-muted);
        }

        /* Categories Filter */
        .blog-categories {
          padding: 60px 2rem;
          background: var(--bg-white);
        }

        .categories-container {
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .category-btn {
          padding: 0.75rem 1.75rem;
          border: 2px solid var(--border);
          border-radius: 50px;
          background: var(--bg-white);
          color: var(--text-dark);
          font-size: 0.95rem;
          font-weight: 600;
          font-family: var(--font-body);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .category-btn:hover {
          border-color: var(--accent);
          color: var(--accent);
          transform: translateY(-2px);
        }

        .category-btn.active {
          background: linear-gradient(135deg, var(--accent) 0%, var(--accent-secondary) 100%);
          color: var(--primary);
          border-color: var(--accent);
          box-shadow: 0 4px 16px rgba(0, 229, 160, 0.3);
        }

        /* Featured Posts */
        .featured-section {
          padding: 60px 2rem 80px;
          background: var(--bg-light);
        }

        .featured-container {
          max-width: 1400px;
          margin: 0 auto;
        }

        .section-label {
          display: inline-block;
          padding: 0.5rem 1.25rem;
          background: rgba(0, 229, 160, 0.1);
          border: 1px solid rgba(0, 229, 160, 0.3);
          border-radius: 50px;
          font-size: 0.85rem;
          font-weight: 700;
          color: var(--primary);
          margin-bottom: 2rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .featured-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
          gap: 2.5rem;
          margin-top: 2rem;
        }

        .featured-post {
          background: var(--bg-white);
          border-radius: 24px;
          overflow: hidden;
          border: 2px solid var(--border);
          transition: all 0.4s ease;
          cursor: pointer;
        }

        .featured-post:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 48px rgba(0, 229, 160, 0.15);
          border-color: var(--accent);
        }

        .featured-post-image {
          width: 100%;
          height: 320px;
          background: linear-gradient(135deg, var(--accent) 0%, var(--accent-secondary) 100%);
          position: relative;
          overflow: hidden;
        }

        .featured-post-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.4s ease;
        }

        .featured-post:hover .featured-post-image img {
          transform: scale(1.05);
        }

        .featured-badge {
          position: absolute;
          top: 1.5rem;
          left: 1.5rem;
          background: var(--accent);
          color: var(--primary);
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.8rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .featured-post-content {
          padding: 2.5rem;
        }

        .post-meta {
          display: flex;
          gap: 1.5rem;
          margin-bottom: 1.25rem;
          flex-wrap: wrap;
        }

        .post-category {
          color: var(--accent);
          font-size: 0.9rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .post-date,
        .post-read-time {
          color: var(--text-muted);
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .featured-post h3 {
          font-family: var(--font-heading);
          font-size: 1.75rem;
          font-weight: 800;
          line-height: 1.3;
          margin-bottom: 1rem;
          color: var(--text-dark);
        }

        .featured-post p {
          color: var(--text-light);
          font-size: 1.05rem;
          line-height: 1.7;
          margin-bottom: 1.5rem;
        }

        .post-author {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--border);
        }

        .author-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--accent) 0%, var(--accent-secondary) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary);
          font-weight: 700;
          font-size: 0.9rem;
        }

        .author-name {
          font-weight: 600;
          color: var(--text-dark);
        }

        /* Regular Posts Grid */
        .posts-section {
          padding: 80px 2rem;
          background: var(--bg-white);
        }

        .posts-container {
          max-width: 1400px;
          margin: 0 auto;
        }

        .posts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
          gap: 2.5rem;
          margin-top: 2rem;
        }

        .blog-post {
          background: var(--bg-light);
          border-radius: 20px;
          overflow: hidden;
          border: 2px solid var(--border);
          transition: all 0.4s ease;
          cursor: pointer;
        }

        .blog-post:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 48px rgba(0, 229, 160, 0.15);
          border-color: var(--accent);
        }

        .blog-post-image {
          width: 100%;
          height: 240px;
          background: linear-gradient(135deg, rgba(0, 229, 160, 0.2) 0%, rgba(0, 184, 212, 0.2) 100%);
          position: relative;
          overflow: hidden;
        }

        .blog-post-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.4s ease;
        }

        .blog-post:hover .blog-post-image img {
          transform: scale(1.05);
        }

        .blog-post-content {
          padding: 2rem;
        }

        .blog-post h3 {
          font-family: var(--font-heading);
          font-size: 1.35rem;
          font-weight: 800;
          line-height: 1.3;
          margin-bottom: 0.75rem;
          color: var(--text-dark);
        }

        .blog-post p {
          color: var(--text-light);
          font-size: 1rem;
          line-height: 1.6;
          margin-bottom: 1.25rem;
        }

        /* No Results */
        .no-results {
          text-align: center;
          padding: 80px 2rem;
          color: var(--text-light);
        }

        .no-results h3 {
          font-size: 1.75rem;
          margin-bottom: 1rem;
          color: var(--text-dark);
        }

        /* CTA Section */
        .blog-cta {
          padding: 100px 2rem;
          background: var(--primary);
          text-align: center;
          color: var(--bg-white);
        }

        .blog-cta-content {
          max-width: 800px;
          margin: 0 auto;
        }

        .blog-cta h2 {
          font-family: var(--font-heading);
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 800;
          margin-bottom: 1.5rem;
          letter-spacing: -0.02em;
        }

        .blog-cta p {
          font-size: 1.2rem;
          margin-bottom: 2.5rem;
          opacity: 0.9;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .featured-grid {
            grid-template-columns: 1fr;
          }

          .posts-grid {
            grid-template-columns: 1fr;
          }

          .categories-container {
            justify-content: flex-start;
            overflow-x: auto;
            padding-bottom: 1rem;
          }

          .blog-hero {
            padding: 120px 1.5rem 60px;
          }
        }
      `}</style>

      {/* Header - reusing from Landing page */}
      <header className="header">
        <nav className="nav-container">
          <a href="/">
            <img src="/images/8EYtClIJaotk40RaLdi5bBD444-1.png" alt="Gerbo Logo" className="logo" />
          </a>
          
          <button className="mobile-menu-toggle" aria-label="Toggle menu" onClick={toggleMobileMenu}>
            ☰
          </button>
          
          <ul className="nav-menu">
            <li><a href="/#features">Features</a></li>
            <li><a href="/#pricing">Pricing</a></li>
            <li><a href="/#industries">Industries</a></li>
            <li><a href="/#faqs">FAQ</a></li>
            <li><a href="/contact">Contact</a></li>
            <li><a href="/blog" style={{ color: 'var(--accent)' }}>Blog</a></li>
            <li>
              <button onClick={() => navigate('/login')} className="btn btn-primary">
                Try Gerbo Live
              </button>
            </li>
          </ul>
        </nav>
      </header>

      {/* Blog Hero */}
      <section className="blog-hero">
        <div className="blog-hero-content">
          <h1>
            Insights & <span className="highlight">Resources</span>
          </h1>
          <p>
            Discover strategies, case studies, and expert advice to help your business capture more leads, 
            improve customer service, and grow with AI-powered solutions.
          </p>

          <div className="blog-search">
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="blog-categories">
        <div className="categories-container">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <section className="featured-section">
          <div className="featured-container">
            <span className="section-label">⭐ Featured Articles</span>
            <div className="featured-grid">
              {featuredPosts.map((post) => (
                <article key={post.id} className="featured-post" onClick={() => navigate(`/blog/${post.id}`)}>
                  <div className="featured-post-image">
                    <div className="featured-badge">Featured</div>
                    {/* Placeholder gradient - replace with actual images */}
                    <div style={{
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(135deg, rgba(0, 229, 160, 0.3) 0%, rgba(0, 184, 212, 0.3) 100%)'
                    }} />
                  </div>
                  <div className="featured-post-content">
                    <div className="post-meta">
                      <span className="post-category">{post.category}</span>
                      <span className="post-date">
                        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                        {post.date}
                      </span>
                      <span className="post-read-time">
                        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        {post.readTime}
                      </span>
                    </div>
                    <h3>{post.title}</h3>
                    <p>{post.excerpt}</p>
                    <div className="post-author">
                      <div className="author-avatar">
                        {post.author.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="author-name">{post.author}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Regular Posts */}
      <section className="posts-section">
        <div className="posts-container">
          {regularPosts.length === 0 && filteredPosts.length === 0 ? (
            <div className="no-results">
              <h3>No articles found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          ) : regularPosts.length > 0 ? (
            <>
              {featuredPosts.length > 0 && (
                <span className="section-label">📚 Latest Articles</span>
              )}
              <div className="posts-grid">
                {regularPosts.map((post) => (
                  <article key={post.id} className="blog-post" onClick={() => navigate(`/blog/${post.id}`)}>
                    <div className="blog-post-image">
                      {/* Placeholder gradient - replace with actual images */}
                      <div style={{
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(135deg, rgba(0, 229, 160, 0.2) 0%, rgba(0, 184, 212, 0.2) 100%)'
                      }} />
                    </div>
                    <div className="blog-post-content">
                      <div className="post-meta">
                        <span className="post-category">{post.category}</span>
                        <span className="post-date">
                          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                          {post.date}
                        </span>
                      </div>
                      <h3>{post.title}</h3>
                      <p>{post.excerpt}</p>
                      <div className="post-author">
                        <div className="author-avatar">
                          {post.author.split(' ').map(n => n[0]).join('')}
                        </div>
                        <span className="author-name">{post.author}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </section>

      {/* CTA Section */}
      <section className="blog-cta">
        <div className="blog-cta-content">
          <h2>Ready to Stop Missing Calls?</h2>
          <p>Join 150+ South African businesses already using Gerbo to capture more leads and grow their revenue.</p>
          <button onClick={() => navigate('/register')} className="btn btn-primary" style={{ marginRight: '1rem' }}>
            Start Free Trial
          </button>
          <a href="/contact" className="btn btn-outline-white">
            Talk to Sales
          </a>
        </div>
      </section>

      {/* Footer - reusing from Landing page */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-column">
              <img src="/images/8EYtClIJaotk40RaLdi5bBD444-1.png" alt="Gerbo Logo" className="logo" style={{ marginBottom: '1.5rem' }} />
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', lineHeight: 1.7 }}>Your friendly AI receptionist, always available to help your business grow.</p>
            </div>

            <div className="footer-column">
              <h4>Product</h4>
              <ul className="footer-links">
                <li><a href="/#features">Features</a></li>
                <li><a href="/#pricing">Pricing</a></li>
                <li><a href="/#industries">Industries</a></li>
                <li><a href="/blog">Blog</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Company</h4>
              <ul className="footer-links">
                <li><a href="/contact">Contact</a></li>
                <li><a href="/privacy">Privacy</a></li>
                <li><a href="/terms">Terms</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Support</h4>
              <ul className="footer-links">
                <li><a href="mailto:support@gerboai.com">support@gerboai.com</a></li>
                
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© 2025 Gerbo. All rights reserved.</p>
            <p>Made by Solid5</p>
          </div>
        </div>
      </footer>
    </>
  );
};